import React, { useState } from 'react';
import _classNames from 'classnames';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import _get from 'lodash/get';
import { toast } from 'react-toastify';
import useSiteCopySelector from 'components/useSiteCopySelector';
import CopyIcon from './icons/Copy';

const useStyles = makeStyles(theme => ({
  copyButton: {
    borderRadius: 8,
    padding: '6px 12px',
    ...theme.typography.pxSmall,
    textTransform: 'none',
    lineHeight: '18px',
    flex: 1,
    textAlign: 'center',
    border: '1px solid #01619B',
  },
  copiedButton: {
    backgroundColor: '#4CAF50', // Green color
    border: '1px solid #4CAF50',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#4CAF50', // Keep same color on hover
      cursor: 'default',
    },
  },
  iconButton: {
    padding: 4,
  },
  copySuccessMessage: {
    color: '#4CAF50',
    marginLeft: 4,
    fontSize: 12,
  },
}));

/**
 * A reusable button component for copying text to clipboard
 */
const CopyButton = ({
  getHTMLContentToCopy,
  getContentToCopy,
  buttonText = 'Copy',
  copiedText,
  variant = 'outlined',
  color = 'primary',
  className,
  copyTextClassName,
  iconButton = false,
  Icon = CopyIcon,
  ariaLabel,
  onCopied,
  size = 'medium',
}) => {
  const [copied, setCopied] = useState(false);
  const classes = useStyles();
  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);
  const theme = useTheme();

  // Default text from site copy if available
  const defaultCopiedText = _get(
    resourceItemPageSiteCopy,
    'pageCopy.copied',
    'Copied!',
  );

  const handleCopySuccess = () => {
    setCopied(true);
    if (onCopied) onCopied();
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyClick = async e => {
    e.stopPropagation();
    const contentToCopy = getContentToCopy();

    if (!navigator.clipboard) {
      toast.error('Clipboard is not supported in this browser');
      return;
    }

    // Use Clipboard API to copy rich content
    if (getHTMLContentToCopy && contentToCopy && window.ClipboardItem) {
      const htmlContent = getHTMLContentToCopy();

      const clipboardItem = new window.ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([contentToCopy], { type: 'text/plain' }),
      });

      navigator.clipboard
        .write([clipboardItem])
        .then(() => {
          handleCopySuccess();
        })
        .catch(() => {
          toast.error('Failed to copy content');
        });

      return;
    }

    if (contentToCopy) {
      navigator.clipboard
        .writeText(contentToCopy)
        .then(() => {
          handleCopySuccess();
        })
        .catch(() => {
          toast.error('Failed to copy content');
        });
    }
  };

  if (iconButton) {
    return (
      <>
        <IconButton
          className={_classNames(classes.iconButton, className)}
          onClick={handleCopyClick}
          aria-label={ariaLabel || `Copy ${buttonText}`}
          size={size}
          color={color}
          disableRipple={copied}
        >
          <Icon color={copied ? '#4CAF50' : theme.palette.primary.main} />
        </IconButton>
        {copied && (
          <span
            className={_classNames(
              classes.copySuccessMessage,
              copyTextClassName,
            )}
          >
            {copiedText || defaultCopiedText}
          </span>
        )}
      </>
    );
  }

  return (
    <Button
      variant={copied ? 'contained' : variant}
      className={_classNames(
        classes.copyButton,
        copied && classes.copiedButton,
        className,
      )}
      onClick={handleCopyClick}
      color={copied ? 'default' : color}
      disableRipple={copied}
      aria-label={copied ? 'Copied' : buttonText}
      size={size}
    >
      {copied ? copiedText || defaultCopiedText : buttonText}
    </Button>
  );
};

export default CopyButton;
